import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, type RouteObject } from 'react-router-dom'
import { LoadingScreen } from '@/components'
import { AppLayout } from '@/layouts/AppLayout'
import { HomePage } from '@/pages/HomePage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { RouteErrorPage } from '@/pages/RouteErrorPage'

const ClientsPage = lazy(() =>
  import('@/features/clients/ClientsPage').then((module) => ({ default: module.ClientsPage })),
)
const ClientFormPage = lazy(() =>
  import('@/features/clients/ClientFormPage').then((module) => ({
    default: module.ClientFormPage,
  })),
)
const ClientDetailsPage = lazy(() =>
  import('@/features/clients/ClientDetailsPage').then((module) => ({
    default: module.ClientDetailsPage,
  })),
)
const MachinesPage = lazy(() =>
  import('@/features/machines/MachinesPage').then((module) => ({ default: module.MachinesPage })),
)
const MachineFormPage = lazy(() =>
  import('@/features/machines/MachineFormPage').then((module) => ({
    default: module.MachineFormPage,
  })),
)
const MachineDetailsPage = lazy(() =>
  import('@/features/machines/MachineDetailsPage').then((module) => ({
    default: module.MachineDetailsPage,
  })),
)
const TechniciansPage = lazy(() =>
  import('@/features/technicians/TechniciansPage').then((module) => ({
    default: module.TechniciansPage,
  })),
)
const TechnicianFormPage = lazy(() =>
  import('@/features/technicians/TechnicianFormPage').then((module) => ({
    default: module.TechnicianFormPage,
  })),
)
const TechnicianDetailsPage = lazy(() =>
  import('@/features/technicians/TechnicianDetailsPage').then((module) => ({
    default: module.TechnicianDetailsPage,
  })),
)
const NewReportPage = lazy(() =>
  import('@/features/inspections/NewReportPage').then((module) => ({
    default: module.NewReportPage,
  })),
)
const ReportDetailsPage = lazy(() =>
  import('@/features/inspections/ReportDetailsPage').then((module) => ({
    default: module.ReportDetailsPage,
  })),
)
const InspectionPage = lazy(() =>
  import('@/features/inspections/InspectionPage').then((module) => ({
    default: module.InspectionPage,
  })),
)
const InspectionSummaryPage = lazy(() =>
  import('@/features/inspections/InspectionSummaryPage').then((module) => ({
    default: module.InspectionSummaryPage,
  })),
)
const ReportsPage = lazy(() =>
  import('@/features/inspections/ReportsPage').then((module) => ({
    default: module.ReportsPage,
  })),
)
const BackupPage = lazy(() =>
  import('@/features/backups/BackupPage').then((module) => ({ default: module.BackupPage })),
)
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage })),
)

function deferred(page: ReactNode) {
  return <Suspense fallback={<LoadingScreen message="Carregando página…" />}>{page}</Suspense>
}

export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <HomePage /> },
      {
        path: 'relatorios/novo',
        element: deferred(<NewReportPage />),
      },
      {
        path: 'relatorios/:id',
        element: deferred(<ReportDetailsPage />),
      },
      {
        path: 'relatorios/:id/inspecao',
        element: deferred(<InspectionPage />),
      },
      {
        path: 'relatorios/:id/resumo',
        element: deferred(<InspectionSummaryPage />),
      },
      {
        path: 'relatorios',
        element: deferred(<ReportsPage />),
      },
      {
        path: 'clientes',
        element: deferred(<ClientsPage />),
      },
      {
        path: 'clientes/novo',
        element: deferred(<ClientFormPage />),
      },
      {
        path: 'clientes/:id/editar',
        element: deferred(<ClientFormPage />),
      },
      {
        path: 'clientes/:id',
        element: deferred(<ClientDetailsPage />),
      },
      {
        path: 'maquinas',
        element: deferred(<MachinesPage />),
      },
      {
        path: 'maquinas/nova',
        element: deferred(<MachineFormPage />),
      },
      {
        path: 'maquinas/:id/editar',
        element: deferred(<MachineFormPage />),
      },
      {
        path: 'maquinas/:id',
        element: deferred(<MachineDetailsPage />),
      },
      {
        path: 'tecnicos',
        element: deferred(<TechniciansPage />),
      },
      {
        path: 'tecnicos/novo',
        element: deferred(<TechnicianFormPage />),
      },
      {
        path: 'tecnicos/:id/editar',
        element: deferred(<TechnicianFormPage />),
      },
      {
        path: 'tecnicos/:id',
        element: deferred(<TechnicianDetailsPage />),
      },
      {
        path: 'backup',
        element: deferred(<BackupPage />),
      },
      {
        path: 'configuracoes',
        element: deferred(<SettingsPage />),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]

export const appRouter = createBrowserRouter(appRoutes)
